import { useContext, useEffect, useState } from 'react';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import CustomInputText from '../../../../../components/CustomInputs/CustomInputText';
import CustomModal from '../../../../../components/CustomModal';
import CustomRow from '../../../../../components/CustomRow';
import CustomSelect from '../../../../../components/CustomSelects/CustomSelect';
import CustomTable from '../../../../../components/CustomTable';
import { TableRow } from '../../../../../components/CustomTable/types/table.type';
import Log from '../../../../../components/Log/Log';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { UserContext } from '../../../../../modules/contexts/Auth/UserProvider';
import { getStatusAtivoMedsusOptions } from '../../../../../modules/types/StatusAtivoMedsus.enum';
import { LogData } from '../../../../../modules/types/types';
import { isHttpError } from '../../../../../services/httpClient';
import { getAllMedsusCriterioByDiretrizVmp } from '../../../../../services/medsus/medsusCriterioService';
import { useStatusCriterio } from '../../../../../services/medsus/useStatusCriterio';
import { useTipoCriterio } from '../../../../../services/medsus/useTipoCriterio';
import theme from '../../../../../theme';
import { ENTITY } from '../../../../../utils/entityNames';
import { handleDelete } from '../../../../../utils/handleDelete';
import { handleSave } from '../../../../../utils/handleSave';
import { headersCriteriosMedsus } from '../auxliarProdutoMedsus';
import { INITIAL_FORM_PRODUTO_MEDSUS_CRITERIO } from '../utils/initialStates';
import { MedsusCriterio } from '../utils/medsusTypes';

interface CriterioProps {
  vmpID: string;
  userID: number;
  btDisable: boolean;
}

export function CriterioMedsus(props: CriterioProps) {
  const entity = ENTITY.ENTITY_Medsus_Criterio;
  const { cudProdutoDaf } = useContext(UserContext);
  // Enums
  const tipoCriterio = useTipoCriterio();
  const statusCriterio = useStatusCriterio();

  // Data
  const [updating, setUpdating] = useState<boolean>(false);
  const [criterioData, setCriterioData] = useState<MedsusCriterio[]>([]);
  const [form, setForm] = useState(
    INITIAL_FORM_PRODUTO_MEDSUS_CRITERIO(
      props.userID,
      Number(props.vmpID),
    ) as MedsusCriterio,
  );
  const [showModalCriterio, setShowModalCriterio] = useState<boolean>(false);

  const [logData, setLogData] = useState<LogData>();

  useEffect(() => {
    if (isProtocoloCreated()) fetchData();
  }, [props.vmpID]);

  // Data Management
  const isProtocoloCreated = (): boolean => {
    return Number(props.vmpID) !== 0;
  };

  const fetchData = async () => {
    try {
      const objetos = await getAllMedsusCriterioByDiretrizVmp(props.vmpID);

      setCriterioData(objetos);
    } catch (error) {
      console.error('Erro ao obter dados: ', error);
    }
  };

  const tableData: TableRow[] = criterioData?.map(
    (criterio: MedsusCriterio) => ({
      id: criterio.id,
      status: statusCriterio[Number(criterio.statusCriterio)]?.label ?? '',
      tipo: tipoCriterio[Number(criterio.tipoCriterio)]?.label ?? '',
      titulo: criterio.tituloCriterio,
      descricao: criterio.descricaoCriterio,
    }),
  );

  const camposObrigatorios = {
    statusCriterio: 'Status',
    tipoCriterio: 'Tipo',
    tituloCriterio: 'Título',
    descricaoCriterio: 'Descrição',
  };

  const camposAjustar = {
    number: ['id', 'statusCriterio', 'tipoCriterio', 'diretrizVmpId'],
  };

  // Handle Functions
  const handleClearForm = () => {
    setForm(
      INITIAL_FORM_PRODUTO_MEDSUS_CRITERIO(props.userID, Number(props.vmpID)),
    );
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleRowClick = (row: TableRow) => {
    handleShowModalCriterio();
    setUpdating(true);
    const filteredItem = criterioData.find((item) => item.id == row.id);

    setLogData({
      id: filteredItem?.id ?? 0,
      usuarioRegistro: filteredItem?.usuarioRegistro?.nomeCompleto || '',
      usuarioAtualizacao: filteredItem?.usuarioAtualizacao?.nomeCompleto || '',
      dataRegistro: filteredItem?.dataRegistro || '',
      dataAtualizacao: filteredItem?.dataUltimaAtualizacao || '',
      nEdicoes: filteredItem?.nEdicoes || 0,
    });
    if (filteredItem) {
      setForm(filteredItem);
    }
  };

  const handleShowModalCriterio = () => {
    setShowModalCriterio((prev) => !prev);
    handleClearForm();
  };

  const handleChangeCriterio = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // REST
  const salvar = async () => {
    try {
      const handleSaveProcessed = await handleSave(
        form,
        camposObrigatorios,
        camposAjustar,
        'medsuscriterio',
        `daf/produtosdaf/medsus/${props.vmpID}/criterios`,
        fetchData,
        false,
      );
      fetchData();
      if (handleSaveProcessed)
        setTimeout(() => handleShowModalCriterio(), 2000);
    } catch (error: any) {
      if (isHttpError(error)) {
        console.error('Erro ao salvar: ', error.response?.data.message);
      }
    }
  };

  const handleDeleteCriterio = async () => {
    await handleDelete(form.id, 'medsuscriterio');
    fetchData();
    handleShowModalCriterio();
  };

  return (
    <BoxConteudo boxTitulo="Critérios de Inclusão e Exclusão" fechado>
      <CustomRow>
        <CustomCol width="100%">
          <CustomTable
            headers={headersCriteriosMedsus}
            data={tableData}
            onRowClick={handleRowClick}
            showPagination={false}
          />
        </CustomCol>
      </CustomRow>

      {cudProdutoDaf && (
        <CustomCol style={{ marginTop: '15px', textAlign: 'right' }}>
          <CustomButton
            label="Novo Critério"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={() => handleShowModalCriterio()}
            disabled={!isProtocoloCreated()}
          />
        </CustomCol>
      )}

      {/* Modal Critério */}
      <CustomModal
        title="Critério de Inclusão/Exclusão"
        open={showModalCriterio}
        onHide={handleShowModalCriterio}
        icone="list_alt"
        width="65%"
      >
        <WorkspaceFicha>
          {logData?.id ? <Log {...logData} entity={entity} /> : null}
          <BoxConteudo boxTitulo="Identificação do Critério">
            <CustomRow
              style={{
                marginTop: '-15px',
                justifyContent: 'flex-start',
              }}
            >
              <CustomCol width="200px">
                <CustomSelect
                  name="statusCriterio"
                  options={getStatusAtivoMedsusOptions()}
                  title="Status"
                  value={form.statusCriterio}
                  onChange={handleChangeCriterio}
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomSelect
                  name="tipoCriterio"
                  options={tipoCriterio}
                  title="Tipo"
                  value={form.tipoCriterio}
                  onChange={handleChangeCriterio}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="tituloCriterio"
                  title="Título do Critério"
                  value={form.tituloCriterio}
                  onChange={handleChangeCriterio}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="descricaoCriterio"
                  title="Descrição"
                  value={form.descricaoCriterio}
                  onChange={handleChangeCriterio}
                  type="textArea"
                  rows={5}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="observacoes"
                  title="Observações"
                  value={form.observacoes ?? ''}
                  type="textArea"
                  onChange={handleChangeCriterio}
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                deleteDisabled={!cudProdutoDaf}
                onClickDelete={handleDeleteCriterio}
                save
                saveDisabled={!cudProdutoDaf}
                // saveDisabled={!props.btDisable}
                onClickSave={salvar}
              />
            ) : (
              <WorkspaceBotoes
                save
                // saveDisabled={!props.btDisable}
                onClickSave={salvar}
                saveDisabled={!cudProdutoDaf}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomModal>
    </BoxConteudo>
  );
}
